<template>
    <BaseModalFront
        :show="show"
        :title="$t('perfil.enviar_entrada')"
        :confirm-text="$t('perfil.enviar_entrada')"
        :cancel-text="$t('perfil.cancelar')"
        confirm-variant="alt"
        :confirm-disabled="!isValid || loading"
        @close="handleClose"
        @confirm="handleSubmit"
    >
        <div class="tw:mb-4">
            <p class="tw:text-[10px] tw:font-bold tw:text-red-500 tw:uppercase tw:tracking-wider tw:m-0 tw:mb-0.5">{{ $t('perfil.evento') }}</p>
            <p class="tw:text-base tw:font-semibold tw:text-gray-900 tw:m-0 tw:mb-3">{{ pedido.evento?.nombre }}</p>

            <div class="tw:grid tw:grid-cols-2 tw:gap-4">
                <div>
                    <p class="tw:text-[10px] tw:font-bold tw:text-gray-400 tw:uppercase tw:tracking-wider tw:m-0 tw:mb-0.5">{{ $t('perfil.entrada_detalle.titular') }}</p>
                    <p class="tw:text-sm tw:text-gray-800 tw:m-0">
                        {{ entrada.user_nombre }} {{ entrada.user_apellidos }}
                    </p>
                </div>
                <div v-if="ubicacion">
                    <p class="tw:text-[10px] tw:font-bold tw:text-gray-400 tw:uppercase tw:tracking-wider tw:m-0 tw:mb-0.5">{{ $t('perfil.entrada_detalle.ubicacion') }}</p>
                    <p class="tw:text-sm tw:text-gray-800 tw:m-0">{{ ubicacion }}</p>
                </div>
            </div>
        </div>

        <div class="tw:flex tw:border-b tw:border-gray-200 tw:-mx-6 tw:px-6 tw:mb-5">
            <button
                type="button"
                class="tw:flex tw:items-center tw:gap-2 tw:px-2 tw:py-3 tw:text-sm tw:font-medium tw:border-b-2 tw:transition-colors"
                :class="activeTab === 'email'
                    ? 'tw:border-red-500 tw:text-red-500'
                    : 'tw:border-transparent tw:text-gray-500 tw:hover:text-gray-700'"
                @click="activeTab = 'email'"
            >
                <i class="material-icons tw:text-base">email</i>
                {{ $t('perfil.send_ticket.email_tab') }}
            </button>
        </div>

        <div v-if="activeTab === 'email'">
            <ValidationObserver v-slot="{ }">
                <div class="tw:space-y-4">
                    <div class="tw:grid tw:grid-cols-2 tw:gap-3">
                        <InputText
                            :value="nombre"
                            :label="$t('formularios.nombre')"
                            name="nombre"
                            placeholder="Ej. Juan"
                            rules="required"
                            :disabled="loading"
                            @input="nombre = $event"
                        />
                        <InputText
                            :value="apellidos"
                            :label="$t('formularios.apellidos')"
                            name="apellidos"
                            placeholder="Ej. García"
                            rules="required"
                            :disabled="loading"
                            @input="apellidos = $event"
                        />
                    </div>
                    <BaseInputEmail
                        :value="email"
                        :label="$t('perfil.email_destino')"
                        name="email"
                        placeholder="nombre@ejemplo.com"
                        rules="required"
                        :disabled="loading"
                        @input="email = $event"
                    />
                </div>
            </ValidationObserver>

            <p class="tw:text-[11px] tw:text-gray-400 tw:m-0 tw:mt-4 tw:leading-relaxed">
                {{ $t('perfil.send_ticket.footer_info') }}
            </p>

            <p v-if="error" class="tw:text-xs tw:text-red-600 tw:font-medium tw:m-0 tw:mt-3">{{ error }}</p>
        </div>
    </BaseModalFront>
</template>

<script setup>
    import { ref, computed, watch } from 'vue';
    import { t } from '@/utils/i18n-helper';
    import BaseModalFront from '@/modules/front/ui/BaseModalFront.vue';
    import BaseInputEmail from '@/modules/front/ui/BaseInputEmail.vue';
    import InputText from '@/modules/front/ui/InputText.vue';
    import { useSendTicket } from '@/composables/front/perfil/useSendTicket';

    const props = defineProps({
        show:    { type: Boolean, required: true },
        entrada: { type: Object,  required: true },
        pedido:  { type: Object,  required: true },
    });

    const emit = defineEmits(['close', 'success']);

    const activeTab = ref('email');

    const { email, nombre, apellidos, isValid, loading, error, send, reset } = useSendTicket();

    const singleQrCode = computed(() => [props.entrada.qr_code].filter(Boolean));

    const ubicacion = computed(() => {
        const a = props.entrada.asiento ?? props.entrada.plaza;
        if (!a) return '';
        const zona = a.region?.nombre ?? a.zona;
        const fila = a.row ?? a.fila;
        const butaca = a.seat ?? a.butaca ?? a.asiento;
        const parts = [zona, fila ? `${t('perfil.fila')} ${fila}` : null, butaca ? `${t('perfil.asiento')} ${butaca}` : null];
        return parts.filter(Boolean).join(' • ');
    });

    const handleSubmit = async() => {
        const ok = await send({ pedidoId: props.pedido.id, qrCodes: singleQrCode.value });
        if (ok) {
            emit('success');
            emit('close');
        }
    };

    const handleClose = () => {
        if (!loading.value) {
            reset();
            emit('close');
        }
    };

    watch(() => props.show, val => {
        if (!val) reset();
    });
</script>
